import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import EventMainReport from '@/model/EventMainReport';
import { EventProductMainReport } from '@/model/EventProductMainReport';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#333333',
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#1B4880',
    borderBottomStyle: 'solid',
    paddingBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1B4880',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#555555',
    marginTop: 2,
  },
  headerMeta: {
    fontSize: 8,
    color: '#666666',
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1B4880',
    backgroundColor: '#F0F4F8',
    padding: '4 8',
    marginTop: 12,
    marginBottom: 6,
    borderRadius: 2,
  },
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
    gap: 6,
  },
  kpiCard: {
    width: '24%',
    backgroundColor: '#FAFAFA',
    borderWidth: 1,
    borderColor: '#EEEEEE',
    borderRadius: 3,
    padding: 6,
  },
  kpiLabel: {
    fontSize: 7,
    color: '#666666',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  kpiValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#222222',
  },
  table: {
    width: '100%',
    marginVertical: 6,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#1B4880',
    color: '#FFFFFF',
    fontWeight: 'bold',
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRowEven: {
    backgroundColor: '#F9FBFD',
  },
  colProduct: { flex: 3 },
  colQty: { flex: 1, textAlign: 'right' },
  colMoney: { flex: 1.5, textAlign: 'right' },
  colStock: { flex: 1.2, textAlign: 'right' },
  colUser: { flex: 2 },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888888',
  },
  signaturesContainer: {
    marginTop: 35,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  signatureBox: {
    width: '40%',
    borderTopWidth: 1,
    borderTopColor: '#444444',
    paddingTop: 4,
    alignItems: 'center',
  },
  signatureText: {
    fontSize: 8,
    color: '#555555',
  },
});

const formatCurrency = (val: number | undefined | null): string => {
  if (val === undefined || val === null) return 'R$ 0,00';
  return `R$ ${Number(val).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

interface ProductReportPDFProps {
  event: EventMainReport;
  main?: EventProductMainReport;
}

export const ProductReportPDF: React.FC<ProductReportPDFProps> = ({ event, main }) => {
  const productData = event.product;

  const totalSoldQty =
    main?.products?.reduce((acc, curr) => acc + (curr.productsSold || 0), 0) || 0;
  const totalSalesVal =
    main?.products?.reduce((acc, curr) => acc + (curr.totalSaleVale || 0), 0) || 0;
  const totalCommVal =
    main?.products?.reduce((acc, curr) => acc + (curr.totalCommission || 0), 0) || 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>Relatório Contábil de Produtos</Text>
            <Text style={styles.headerSubtitle}>{event.name || 'Evento sem nome'}</Text>
          </View>
          <View style={styles.headerMeta}>
            <Text>Data Evento: {event.date ? dayjs(event.date).format('DD/MM/YYYY') : 'N/I'}</Text>
            <Text>Emissão: {dayjs().format('DD/MM/YYYY HH:mm')}</Text>
          </View>
        </View>

        {/* Seção 1: Resumo Financeiro */}
        <Text style={styles.sectionTitle}>1. Resumo de Vendas de Produtos</Text>
        <View style={styles.kpiGrid}>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Qtd. Vendas</Text>
            <Text style={styles.kpiValue}>{productData?.amountSale ?? totalSoldQty}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Cortesias</Text>
            <Text style={styles.kpiValue}>{productData?.amountCourtesy ?? 0}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Descontos</Text>
            <Text style={styles.kpiValue}>{formatCurrency(productData?.discount)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Ticket Médio</Text>
            <Text style={styles.kpiValue}>{formatCurrency(productData?.averageTicket)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Total Vendas</Text>
            <Text style={styles.kpiValue}>
              {formatCurrency(productData?.saleValue ?? totalSalesVal)}
            </Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Comissão</Text>
            <Text style={styles.kpiValue}>{formatCurrency(productData?.fee ?? totalCommVal)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Vendas + Comissão</Text>
            <Text style={styles.kpiValue}>
              {formatCurrency((productData?.saleValue || 0) + (productData?.fee || 0))}
            </Text>
          </View>
        </View>

        {/* Seção 2: Vendas Gerais por Produto */}
        {main?.products && main.products.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>2. Vendas Gerais por Produto</Text>
            <View style={styles.table}>
              <View style={styles.tableHeaderRow}>
                <Text style={styles.colProduct}>Produto</Text>
                <Text style={styles.colQty}>Vendidos</Text>
                <Text style={styles.colMoney}>V. Unit</Text>
                <Text style={styles.colMoney}>Venda</Text>
                <Text style={styles.colMoney}>Comissão</Text>
                <Text style={styles.colStock}>Estoque Inc.</Text>
                <Text style={styles.colStock}>Estoque Disp.</Text>
              </View>

              {main.products.map((prod, index) => (
                <View
                  key={`prod-${index}`}
                  style={[styles.tableRow, index % 2 === 1 ? styles.tableRowEven : {}]}
                >
                  <Text style={styles.colProduct}>{prod.productName}</Text>
                  <Text style={styles.colQty}>{prod.productsSold}</Text>
                  <Text style={styles.colMoney}>{formatCurrency(prod.productValue)}</Text>
                  <Text style={styles.colMoney}>{formatCurrency(prod.totalSaleVale)}</Text>
                  <Text style={styles.colMoney}>{formatCurrency(prod.totalCommission)}</Text>
                  <Text style={styles.colStock}>{prod.initialInventory || 0}</Text>
                  <Text style={styles.colStock}>{prod.availableStock || 0}</Text>
                </View>
              ))}

              {/* Subtotal da tabela */}
              <View style={[styles.tableRow, { backgroundColor: '#EAEFF5', fontWeight: 'bold' }]}>
                <Text style={styles.colProduct}>TOTAL GERAL</Text>
                <Text style={styles.colQty}>{totalSoldQty}</Text>
                <Text style={styles.colMoney}>-</Text>
                <Text style={styles.colMoney}>{formatCurrency(totalSalesVal)}</Text>
                <Text style={styles.colMoney}>{formatCurrency(totalCommVal)}</Text>
                <Text style={styles.colStock}>-</Text>
                <Text style={styles.colStock}>-</Text>
              </View>
            </View>
          </View>
        )}

        {/* Seção 3: Cortesias */}
        {main?.courtesies && main.courtesies.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>3. Cortesias Emitidas por Usuário</Text>
            {main.courtesies.map((userCourts, uIdx) => (
              <View key={`uCourt-${uIdx}`} style={{ marginBottom: 6 }}>
                <Text style={{ fontWeight: 'bold', fontSize: 8, marginBottom: 2 }}>
                  Usuário: {userCourts.userName || 'Não Identificado'} (Total Cortesias:{' '}
                  {userCourts.totalProductsSold})
                </Text>
                <View style={styles.table}>
                  <View style={styles.tableHeaderRow}>
                    <Text style={styles.colProduct}>Produto</Text>
                    <Text style={styles.colQty}>Qtd. Emitida</Text>
                  </View>
                  {userCourts.products?.map((cProd, cIdx) => (
                    <View key={`cProd-${cIdx}`} style={styles.tableRow}>
                      <Text style={styles.colProduct}>{cProd.productName}</Text>
                      <Text style={styles.colQty}>{cProd.productsSold}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Rodapé Fixo */}
        <View style={styles.footer} fixed>
          <Text>Banca do Ingresso - Relatório de Fechamento Contábil de Produtos</Text>
          <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};
